<ng-container *transloco="let t">
  @if (trip.isLoading()) {
    <div class="loading-container">
      <div class="loading-text">{{ t('loading') }}...</div>
    </div>
  }

  @if (trip.value(); as tripData) {
    <!-- Header -->
    <header class="trip-header">
      <a href="https://questatlas.world" target="_blank" class="logo-link">
        <img src="assets/vectors/logo.svg" alt="QuestAtlas" class="logo" />
      </a>
      <button class="share-btn" (click)="shareTrip()" [disabled]="isSharing()">
        <q-svg-wrapper iconPath="project-icons/share" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
        {{ t('share') }}
      </button>
    </header>

    <div class="trip-view-container">
      <!-- Hero Section -->
      <div class="hero-section"
           [style.background-image]="tripData.tripConfig?.titleImageUrl ? 'url(' + tripData.tripConfig.titleImageUrl + ')' : null"
           [class.clickable-hero]="tripData.tripConfig?.titleImageUrl"
           (click)="tripData.tripConfig?.titleImageUrl && imageUrlOnPreview.set(tripData.tripConfig.titleImageUrl)">
        <div class="hero-overlay">
          <div class="hero-content">
            <h1 class="trip-title">{{ tripData.name }}</h1>
            @if (tripData.description) {
              <p class="trip-subtitle">{{ tripData.description }}</p>
            }
          </div>
        </div>
      </div>

      <!-- Stats Section -->
      @if (tripData.tripConfig?.stats) {
        <div class="stats-section">
          <div class="stats-container">
            <div class="stats-header">
              <h2 class="section-title">{{ t('tripStats') }}</h2>
              @if (tripData.activityTags && tripData.activityTags.length > 0) {
                <div class="activity-tags-inline">
                  @for (tag of tripData.activityTags; track tag) {
                    <span class="activity-tag">{{ t(tag) }}</span>
                  }
                </div>
              }
            </div>
            <div class="stats-grid">
              @if (formattedDistance()) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/distance" iconId="d" width="28" height="28" viewBox="0 0 20 20"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('distance') }}</div>
                    <div class="stat-value">{{ formattedDistance() }} <span class="stat-unit">{{ t('kilometerShort') }}</span></div>
                  </div>
                </div>
              }

              @if (formattedTime()) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/clock" width="28" height="28" viewBox="0 0 20 20"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('duration') }}</div>
                    <div class="stat-value">{{ formattedTime() }}</div>
                  </div>
                </div>
              }

              @if (formattedElevationGain()) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/arrow" iconId="a" width="28" height="28" viewBox="0 0 24 24" style="transform: rotate(-90deg)"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('elevationGain') }}</div>
                    <div class="stat-value">{{ formattedElevationGain() }} <span class="stat-unit">{{ t('meterShort') }}</span></div>
                  </div>
                </div>
              }

              @if (formattedElevationLoss()) {
                <div class="stat-card">
                  <div class="stat-icon descend">
                    <q-svg-wrapper iconPath="project-icons/arrow" iconId="a" width="28" height="28" viewBox="0 0 24 24" style="transform: rotate(-90deg)"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('elevationLoss') }}</div>
                    <div class="stat-value">{{ formattedElevationLoss() }} <span class="stat-unit">{{ t('meterShort') }}</span></div>
                  </div>
                </div>
              }

              @if (tripData.tripConfig.stats.pavedRoadsPercent !== undefined) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/pie-chart" id="c" width="28" height="28" viewBox="0 0 24 24"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('pavedRoad') }}</div>
                    <div class="stat-value">{{ tripData.tripConfig.stats.pavedRoadsPercent }}<span class="stat-unit">%</span></div>
                  </div>
                </div>
              }

              @if (tripData.tripConfig.stats.unpavedRoadsPercent !== undefined) {
                <div class="stat-card">
                  <div class="stat-icon">
                    <q-svg-wrapper iconPath="project-icons/pie-chart" width="28" height="28" viewBox="0 0 24 24"></q-svg-wrapper>
                  </div>
                  <div class="stat-content">
                    <div class="stat-label">{{ t('unpavedRoad') }}</div>
                    <div class="stat-value">{{ tripData.tripConfig.stats.unpavedRoadsPercent }}<span class="stat-unit">%</span></div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      }

      <!-- App Download Section -->
      <div class="app-download-section">
        <div class="app-download-container">
          <div class="app-download-content">
            <h3 class="app-download-title">{{ t('followThisTripInOurApp') }}</h3>
            <p class="app-download-subtitle">{{ t('travelWithUs') }}</p>
          </div>
          <div class="app-download-buttons">
            <a href="https://apps.apple.com/ua/app/questatlas/id6461825089" target="_blank" rel="noopener noreferrer" class="app-store-link">
              <img style="width: 150px; height: 50px" src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="bn45"/>
            </a>
            <a href="https://play.google.com/store/apps/details?id=world.app.questatlas" target="_blank" rel="noopener noreferrer" class="app-store-link">
              <img style="width: 170px; height: 50px" src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/2560px-Google_Play_Store_badge_EN.svg.png" alt="bn45"/>
            </a>
          </div>
        </div>
      </div>

      <!-- Map Section -->
      @if (tripData.tripConfig && (tripData.quests?.length > 0 || tripData.obelisks?.length > 0 || tripData.tripConfig.paths?.length > 0 || tripData.tripConfig.poi?.length > 0 || tripData.tripConfig.locations?.length > 0)) {
        <div class="map-section" #mapSection>
          <div class="content-container">
            <h2 class="section-title">{{ t('map') }}</h2>
            <div class="map-wrapper">
              <app-display-map
                [options]="mapOptions()"
                (mapInit)="onMapInit($any($event))"
                (clickOnQuest)="onQuestClick($any($event))"
                (clickOnObelisk)="onObeliskClick($any($event))">
              </app-display-map>
            </div>
          </div>
        </div>
      }

      <!-- Locations Section -->
      @if (tripData.tripConfig?.locations && tripData.tripConfig.locations.length > 0) {
        <div class="locations-section">
          <div class="content-container">
            <h2 class="section-title">{{ t('locations') }}</h2>

            @for (location of tripData.tripConfig.locations; track location.name; let i = $index) {
              <div class="location-card">
                <div class="location-header">
                  <div class="location-number">{{ i + 1 }}</div>
                  <h3 class="location-name">{{ location.name }}</h3>
                </div>

                <!-- Location Sections -->
                @if (location.sections && location.sections.length > 0) {
                  <div class="location-sections">
                    @for (section of location.sections; track $index) {
                      <div class="section-content">
                        @if (section.title) {
                          <h4 class="section-subtitle">{{ section.title }}</h4>
                        }
                        @if (section.subtitle) {
                          <p class="section-text-subtitle">{{ section.subtitle }}</p>
                        }
                        @if (section.description) {
                          <p class="section-description">{{ section.description }}</p>
                        }
                        @if (getSectionImages(section).length > 0) {
                          <div class="section-images">
                            @for (img of getSectionImages(section); track $index) {
                              <div class="image-wrapper">
                                <img [src]="img.url" [alt]="img.caption" (click)="imageUrlOnPreview.set(img.url)" />
                                @if (img.caption) {
                                  <div class="image-caption">{{ img.caption }}</div>
                                }
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                }

                <!-- Quests/Obelisks in this location -->
                @if (location.clusters && location.clusters.length > 0) {
                  <div class="clusters-section">
                    <div class="clusters-grid">
                      @for (cluster of location.clusters; track $index) {
                        <div class="cluster-card" (click)="onClusterClick(cluster)">
                          @if (hasClusterImage(cluster)) {
                            <div class="cluster-image-container">
                              <img [src]="getClusterImage(cluster)" [alt]="cluster.name" />
                            </div>
                          } @else {
                            <div class="cluster-icon">
                              @if (isQuest(cluster)) {
                                <svg viewBox="0 0 24 24">
                                  <use [attr.xlink:href]="'assets/project-icons/map.svg#c'"></use>
                                </svg>
                              } @else {
                                <svg viewBox="0 0 24 24">
                                  <use [attr.xlink:href]="'assets/project-icons/place.svg#c'"></use>
                                </svg>
                              }
                            </div>
                          }
                          <div class="cluster-name">{{ cluster.name }}</div>
                        </div>
                      }
                    </div>
                  </div>
                }

                <!-- Points of Interest -->
                @if (location.poi && location.poi.length > 0) {
                  <div class="poi-section">
                    <h4 class="poi-title">{{ t('pointsOfInterest') }}</h4>
                    <div class="poi-grid">
                      @for (poi of location.poi; track poi.uuid || poi.name) {
                        <div class="poi-card">
                          <div class="poi-icon-image">
                            <img [src]="'/assets/maki/' + poi.icon + '.svg'" [alt]="poi.name" />
                          </div>
                          <div class="poi-content">
                            <div class="poi-name">{{ poi.name }}</div>
                            @if (poi.description) {
                              <div class="poi-description">{{ poi.description }}</div>
                            }
                            @if (poi.website) {
                              <a [href]="poi.website" target="_blank" class="poi-website">
                                {{ t('visitWebsite') }}
                                <svg viewBox="0 0 24 24">
                                  <use [attr.xlink:href]="'assets/project-icons/link.svg#c'"></use>
                                </svg>
                              </a>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }

      <!-- Quests & Obelisks Section (if not in locations) -->
      @if (loadedQuests().length > 0 || loadedObelisks().length > 0) {
        <div class="activities-section">
          <div class="content-container">
            <h2 class="section-title">{{ allActivitiesSectionTitle() }}</h2>

            @if (loadedQuests().length > 0) {
              <div class="quests-grid">
                @for (quest of loadedQuests(); track quest.id) {
                  <div class="activity-card" (click)="onQuestCardClick(quest)">
                    <div class="activity-content">
                      <div class="activity-name-container">
                        <div class="cluster-image-container">
                          @if (quest?.info?.images.length) {
                            <img [src]="quest.info?.images[0]?.url" alt="" />
                          }
                        </div>
                        <h4 class="activity-name">{{ quest.name }}</h4>
                      </div>
                      @if (quest.info?.address) {
                        <div class="activity-address">
                          <svg viewBox="0 0 24 24">
                            <use [attr.xlink:href]="'assets/project-icons/location.svg#c'"></use>
                          </svg>
                          {{ quest.info.address }}
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            }

            @if (loadedObelisks().length > 0) {
              <div class="obelisks-grid">
                @for (obelisk of loadedObelisks(); track $index) {
                  <div class="activity-card" (click)="onObeliskCardClick(obelisk)">
                    <div class="activity-content">
                      <div class="activity-name-container">
                        <div class="cluster-image-container">
                          @if (obelisk?.images.length) {
                            <img [src]="obeliskImageUri(obelisk)" alt="" />
                          }
                        </div>
                        <h4 class="activity-name">{{ obelisk.name }}</h4>
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Floating Buttons for Small Screens -->
    @if ((selectedQuest() || selectedObelisk()) && smallWidth()) {
      <button class="floating-secondary-button back-btn"
              (click)="closeSidepanel()">
        <svg viewBox="0 0 24 24">
          <use [attr.xlink:href]="'assets/project-icons/chevron-left.svg#c'"></use>
        </svg>
        {{ t('back') }}
      </button>

      <button class="floating-secondary-button desc-btn"
              (click)="sidepanelOpenOnSmall.set(true)">{{ t('description') }}</button>
    }
  }

  <!-- Sidepanel -->
  @if (!smallWidth() || sidepanelOpenOnSmall()) {
    @if (selectedQuest()) {
      <div class="side-panel">
        <app-quest-view
          [quest]="selectedQuest()!"
          (previewImage)="imageUrlOnPreview.set($any($event))"
          (closeQuest)="closeQuestSidepanel()"
          (hideEvent)="sidepanelOpenOnSmall.set(false)">
        </app-quest-view>
      </div>
    } @else if (selectedObelisk()) {
      <div class="side-panel">
        <app-obelisk-view
          [obelisk]="selectedObelisk()!"
          (previewImage)="imageUrlOnPreview.set($any($event))"
          (closeObelisk)="closeObeliskSidepanel()"
          (hideEvent)="sidepanelOpenOnSmall.set(false)">
        </app-obelisk-view>
      </div>
    }
  }

  <!-- Image Preview -->
  @if (imageUrlOnPreview()) {
    <div class="image-view-container" (click)="imageUrlOnPreview.set(null); $event.stopPropagation()">
      <svg class="close-icon" viewBox="0 0 24 24" (click)="imageUrlOnPreview.set(null); $event.stopPropagation()">
        <use [attr.xlink:href]="'assets/project-icons/close.svg#c'"></use>
      </svg>
      <img [src]="imageUrlOnPreview()!" alt="Preview" (click)="$event.stopPropagation()">
    </div>
  }
</ng-container>


